import { isUndefined, omit, pick, some } from 'lodash-es'
import { getCollectionList } from '@/components/search/filters/utils/exclusive-collection-operator'
import { VerifiedKol } from '@/constants/verified-kol'
import {
  KolRadarSearchDefaultSearchKeys,
  KolRadarSearchRequestBasicPropKey,
  KolRadarSearchRequestBasicProps,
  KolRadarSearchRequestEventProps,
} from '@/types/api/search'
import { ampli, type SearchForKolProperties } from '@/utils/ampli'
import { Potential } from '@/utils/detail/potential'
import { isNilOrEmptyString } from '@/utils/omit-nil-or-empty-string'
import mapEventPropertyFilterFollowerRange from '@/utils/search/map-event-property-filter-follower-range'
import { getFilterOfficialValues } from '@/utils/tracking/get-filter-official-value'

const aiBasicRequestParamKeys: KolRadarSearchRequestBasicPropKey[] = [
  'keyword',
  'customized_tags_names',
  'gender',
  'follower_start_from',
  'follower_end_to',
  'interactiveRateBegin',
  'interactiveRateEnd',
  'fansUpRateBegin',
  'fansUpRateEnd',
  'avgPvRateBegin',
  'avgPvRateEnd',
  'minQuotationPrice',
  'maxQuotationPrice',
  'audienceGenderCode',
  'audienceGenderRateBegin',
  'audienceGenderRateEnd',
  'audienceAgeCode',
  'audienceAgeRateBegin',
  'audienceAgeRateEnd',
  'audienceGeoCode',
  'audienceGeoRateBegin',
  'audienceGeoRateEnd',
]

export const hasAnyFilter = (
  query: KolRadarSearchRequestBasicProps,
): boolean => {
  const isAnyFilter = some(
    omit(query, KolRadarSearchDefaultSearchKeys),
    (value) => !isNilOrEmptyString(value),
  )
  return isAnyFilter
}

const searchForKol = (
  query: KolRadarSearchRequestBasicProps,
  eventProps: KolRadarSearchRequestEventProps,
): void => {
  const isAnyFilter = hasAnyFilter(query)

  const isAIFilterTrigger = some(
    pick(query, aiBasicRequestParamKeys),
    (value) => !isNilOrEmptyString(value),
  )

  const {
    filterCollectionType,
    filterCollectionCount,
    filterCollectionIds,
    filterCollectionNames,
  } = ((): Pick<
    SearchForKolProperties,
    | 'filterCollectionType'
    | 'filterCollectionCount'
    | 'filterCollectionIds'
    | 'filterCollectionNames'
  > => {
    if (isUndefined(query.filter_collection)) {
      return {
        filterCollectionType: undefined,
        filterCollectionCount: undefined,
        filterCollectionIds: undefined,
        filterCollectionNames: undefined,
      }
    }

    const filterCollectionIds = query.exclude_kol_collection_folder_ids ?? ''
    const filterCollectionList =
      getCollectionList(filterCollectionIds.split(',')) ?? []

    if (filterCollectionList === 'all') {
      return {
        filterCollectionType: 'All',
        filterCollectionCount: undefined,
        filterCollectionIds: undefined,
        filterCollectionNames: undefined,
      }
    }

    return {
      filterCollectionType: 'Partial',
      filterCollectionCount: filterCollectionList.length,
      filterCollectionIds: filterCollectionList.map((item) => item.id),
      filterCollectionNames: filterCollectionList.map((item) => item.name),
    }
  })()

  ampli.searchForKol({
    audienceAge: query.audienceAgeCode as SearchForKolProperties['audienceAge'],
    audienceAgeRateBegin: query.audienceAgeRateBegin,
    audienceAgeRateEnd: query.audienceAgeRateEnd,
    audienceGender:
      query.audienceGenderCode as SearchForKolProperties['audienceGender'],
    audienceGenderRateBegin: query.audienceGenderRateBegin,
    audienceGenderRateEnd: query.audienceGenderRateEnd,
    audienceGeo: query.audienceGeoCode,
    audienceGeoRateBegin: query.audienceGeoRateBegin,
    audienceGeoRateEnd: query.audienceGeoRateEnd,
    avgPvRateBegin: query.avgPvRateBegin,
    avgPvRateEnd: query.avgPvRateEnd,
    budgetBegin: query.minQuotationPrice,
    budgetEnd: query.maxQuotationPrice,
    countryCode: query.country_code,
    fansUpRateBegin: query.fansUpRateBegin,
    fansUpRateEnd: query.fansUpRateEnd,
    filterCollection: !isUndefined(query.filter_collection)
      ? query.filter_collection.toString() === 'true'
      : undefined,
    filterCooperation: !isUndefined(query.filter_cooperation)
      ? query.filter_cooperation.toString() === 'true'
      : undefined,
    filterOfficialAccount: getFilterOfficialValues(query.filter_kol_type),
    filterUnrecommend: !isUndefined(query.filter_unrecommended)
      ? query.filter_unrecommended.toString() === 'true'
      : undefined,
    filterVerified:
      query.verifiedKol === VerifiedKol.Any ? undefined : query.verifiedKol,
    followerEndTo: query.follower_end_to,
    followerStartFrom: query.follower_start_from,
    gender: query.gender,
    growthPotentialType:
      query.potential === Potential.Any ? undefined : query.potential,
    interactiveRateBegin: query.interactiveRateBegin,
    interactiveRateEnd: query.interactiveRateEnd,
    isDefault: eventProps.isDefault,
    keyword: query.keyword,
    kolType: query.customized_tags_names?.split('|'),
    platform: query.platform_type ?? 'All',
    postEndDate: query.post_end_time,
    postStartDate: query.post_start_time,
    sort: query.sort,
    view: eventProps.view,
    filterKolLanguage: query.languages?.split('|'),
    keywordFrom: query.keywordFrom,
    from: query.from,
    isAnyFilter,
    isAIFilterTrigger,
    aiEnabled: eventProps.aiEnabled,
    filterFollowerRange: mapEventPropertyFilterFollowerRange(query),
    isExternalLink: eventProps.isExternalLink,
    searchId: eventProps.searchId,
    filterNonRecommended: query.filter_unrecommended === 'true',
    filterCollectionType,
    filterCollectionCount,
    filterCollectionIds,
    filterCollectionNames,
  })
}

export default searchForKol
